import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  listAllEntities,
  updateEntity,
  syncEntitiesNow,
  type MonthendSyncSummary,
} from '@/api/entities';
import { listAllDivisions, updateDivision } from '@/api/divisions';
import { listAllAssignments, type AssignmentWithDetails } from '@/api/auth';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import IconButton from '@/components/ui/IconButton';
import Spinner from '@/components/ui/Spinner';
import { Eye, EyeOff, Pencil, RefreshCw } from 'lucide-react';
import type { Entity } from '@/types';

export default function EntitiesAdminPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [syncSummary, setSyncSummary] = useState<MonthendSyncSummary | null>(null);
  const [qlikEdit, setQlikEdit] = useState<Entity | null>(null);
  const [qlikValue, setQlikValue] = useState('');

  const { data: divisions, isLoading: divisionsLoading } = useQuery({
    queryKey: ['admin-divisions'],
    queryFn: listAllDivisions,
  });

  const { data: entities, isLoading: entitiesLoading } = useQuery({
    queryKey: ['admin-entities'],
    queryFn: listAllEntities,
  });

  // All assignments, for the who-prepares/reviews-what view (MH round-2 item 3).
  const { data: allAssignments } = useQuery({
    queryKey: ['admin-assignments'],
    queryFn: listAllAssignments,
  });

  const displayName = (a: AssignmentWithDetails) => a.user.name ?? a.user.email;
  const byEntity = new Map<string, AssignmentWithDetails[]>();
  const byDivision = new Map<string, AssignmentWithDetails[]>();
  for (const a of allAssignments ?? []) {
    if (a.entityId) byEntity.set(a.entityId, [...(byEntity.get(a.entityId) ?? []), a]);
    if (a.divisionId) byDivision.set(a.divisionId, [...(byDivision.get(a.divisionId) ?? []), a]);
  }

  const assignmentChips = (list: AssignmentWithDetails[], suffix = '') =>
    list.map((a) => (
      <span
        key={a.id}
        className={clsx(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
          a.role === 'PREPARER' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-800',
        )}
        title={`${a.role === 'PREPARER' ? 'Preparer' : 'Reviewer'}: ${displayName(a)}${suffix ? ' (via division assignment)' : ''}`}
      >
        {a.role === 'PREPARER' ? 'P' : 'R'} · {displayName(a)}{suffix}
      </span>
    ));

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-divisions'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-entities'] });
    void queryClient.invalidateQueries({ queryKey: ['entities'] });
    void queryClient.invalidateQueries({ queryKey: ['validation-board'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-assignments'] });
  };

  const onError = (err: unknown) => {
    const msg =
      (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Update failed';
    setError(msg);
  };

  const entityMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateEntity>[1] }) =>
      updateEntity(id, patch),
    onSuccess: invalidate,
    onError,
  });

  const divisionMutation = useMutation({
    mutationFn: ({ id, isHidden }: { id: string; isHidden: boolean }) =>
      updateDivision(id, { isHidden }),
    onSuccess: invalidate,
    onError,
  });

  const syncMutation = useMutation({
    mutationFn: syncEntitiesNow,
    onSuccess: (summary) => {
      setSyncSummary(summary);
      invalidate();
    },
    onError,
  });

  const openQlikEdit = (entity: Entity) => {
    setQlikEdit(entity);
    setQlikValue(entity.qlikName ?? '');
  };

  const saveQlikName = () => {
    if (!qlikEdit) return;
    entityMutation.mutate({
      id: qlikEdit.id,
      patch: { qlikName: qlikValue.trim() === '' ? null : qlikValue.trim() },
    });
    setQlikEdit(null);
  };

  const isLoading = divisionsLoading || entitiesLoading;
  const entitiesByDivision = new Map<string, Entity[]>();
  for (const e of entities ?? []) {
    const list = entitiesByDivision.get(e.divisionId) ?? [];
    list.push(e);
    entitiesByDivision.set(e.divisionId, list);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Entities &amp; Divisions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Master data is owned by the month-end close app and synced hourly.
            Here you can hide entities or divisions from the board and lists,
            and map each entity&apos;s Qlik name.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          loading={syncMutation.isPending}
          onClick={() => { setError(''); setSyncSummary(null); syncMutation.mutate(); }}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Sync from month-end app
        </Button>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {syncSummary && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          Sync complete — divisions: {syncSummary.divisions.created} created,{' '}
          {syncSummary.divisions.updated} updated; entities:{' '}
          {syncSummary.entities.created} created, {syncSummary.entities.updated} updated.
          {syncSummary.entities.needQlikName.length > 0 && (
            <span>
              {' '}New entities needing a Qlik name:{' '}
              <strong>{syncSummary.entities.needQlikName.join(', ')}</strong>
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : (
        (divisions ?? []).map((division) => {
          const divEntities = entitiesByDivision.get(division.id) ?? [];
          return (
            <div
              key={division.id}
              className={clsx(
                'rounded-xl border bg-white shadow-sm',
                division.isHidden ? 'border-gray-300 opacity-60' : 'border-gray-200',
              )}
            >
              {/* Division header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900">{division.name}</span>
                  <span className="text-xs text-gray-500">
                    {divEntities.length} entit{divEntities.length === 1 ? 'y' : 'ies'}
                  </span>
                  {division.isHidden && <Badge variant="grey">Hidden</Badge>}
                  <span className="flex flex-wrap items-center gap-1">
                    {assignmentChips(byDivision.get(division.id) ?? [])}
                  </span>
                </div>
                <IconButton
                  icon={division.isHidden ? Eye : EyeOff}
                  label={
                    division.isHidden
                      ? `Show ${division.name} on the board and lists`
                      : `Hide ${division.name} from the board and lists`
                  }
                  variant={division.isHidden ? 'primary' : 'default'}
                  onClick={() => {
                    setError('');
                    divisionMutation.mutate({ id: division.id, isHidden: !division.isHidden });
                  }}
                />
              </div>

              {/* Entities */}
              {divEntities.length > 0 && (
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-gray-50">
                    {divEntities.map((entity) => (
                      <tr
                        key={entity.id}
                        className={clsx('hover:bg-gray-50', entity.isHidden && 'opacity-60')}
                      >
                        <td className="px-4 py-2 w-20 font-mono text-xs text-gray-600">
                          {entity.code}
                        </td>
                        <td className="px-4 py-2 font-medium text-gray-900">
                          {entity.name}
                          {!entity.isActive && (
                            <Badge variant="grey" className="ml-2">Inactive</Badge>
                          )}
                          {entity.isHidden && (
                            <Badge variant="grey" className="ml-2">Hidden</Badge>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <span className="flex flex-wrap items-center gap-1">
                            {assignmentChips(byEntity.get(entity.id) ?? [])}
                            {assignmentChips(byDivision.get(entity.divisionId) ?? [], ' (div)')}
                            {(byEntity.get(entity.id) ?? []).length === 0 &&
                              (byDivision.get(entity.divisionId) ?? []).length === 0 && (
                                <span className="text-[11px] italic text-gray-400">No assignments</span>
                              )}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          <span className="flex items-center gap-1.5">
                            <span className={clsx('text-xs', !entity.qlikName && 'italic text-amber-700')}>
                              {entity.qlikName ?? 'No Qlik name — derived lines will stay empty'}
                            </span>
                            <IconButton
                              icon={Pencil}
                              label={`Edit Qlik name for ${entity.name}`}
                              onClick={() => openQlikEdit(entity)}
                            />
                          </span>
                        </td>
                        <td className="px-4 py-2 w-12 text-right">
                          <IconButton
                            icon={entity.isHidden ? Eye : EyeOff}
                            label={
                              entity.isHidden
                                ? `Show ${entity.name} on the board and lists`
                                : `Hide ${entity.name} from the board and lists`
                            }
                            variant={entity.isHidden ? 'primary' : 'default'}
                            onClick={() => {
                              setError('');
                              entityMutation.mutate({
                                id: entity.id,
                                patch: { isHidden: !entity.isHidden },
                              });
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}

      {/* Qlik name editor */}
      <Modal
        open={qlikEdit !== null}
        onClose={() => setQlikEdit(null)}
        title={qlikEdit ? `Qlik name — ${qlikEdit.name}` : 'Qlik name'}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="sm" onClick={() => setQlikEdit(null)}>
              Cancel
            </Button>
            <Button size="sm" loading={entityMutation.isPending} onClick={saveQlikName}>
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            The Qlik <strong>Nominal Entity</strong> value for this company.
            Inbound balance-sheet rows only match this entity when the value is
            set exactly as it appears in Qlik. Leave blank to unset.
          </p>
          <Input
            label="Qlik name"
            value={qlikValue}
            onChange={(e) => setQlikValue(e.target.value)}
            placeholder="e.g. Jinky_Services"
          />
        </div>
      </Modal>
    </div>
  );
}
